import { Accordion, AccordionItem } from '@heroui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { permissionsApi, usersApi, type UserDetail } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ErrorState,
  LoadingState,
  SearchField,
  SegmentedControl,
  StatusBadge,
  TextAreaField,
} from '../../components/ui/index.js';
import { groupByModule } from './permission-groups.js';
import { PermissionName } from './PermissionSummary.js';

/**
 * The permission editor.
 *
 * Each permission has three states, which mirror exactly how the backend
 * resolves them:
 *
 *   default — take whatever the role gives
 *   grant   — give it regardless of the role
 *   revoke  — take it away regardless of the role
 *
 * Revocation always wins, so a permission can be removed from anyone,
 * including one their role would otherwise imply. A manager can therefore
 * adjust any individual permission for any user WITHOUT inventing a new role,
 * which is what the requirements ask for (p.20).
 *
 * `results.review` and `results.approve` are ordinary rows here, toggled
 * independently. The editor calls that out in the interface, because treating
 * them as one switch is the mistake the requirements warn against.
 *
 * LAYOUT. Permissions are grouped into modules (`permission-groups.ts`), each
 * row naming the permission in the reader's language with its key beneath,
 * what the role gives, what the person ends up with, and the three-way choice.
 * A search narrows the rows by name or key. Nothing here decides anything:
 * the server resolves and enforces the same three states.
 */

type Effect = 'default' | 'grant' | 'revoke';

export function PermissionEditor({
  user,
  canEdit,
  onSaved,
}: {
  user: UserDetail;
  canEdit: boolean;
  onSaved: () => Promise<void> | void;
}) {
  const { t } = useTranslation();

  const catalogue = useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: permissionsApi.catalogue,
    staleTime: 60 * 60 * 1000,
  });

  const [effects, setEffects] = useState<Record<string, Effect>>({});
  const [reason, setReason] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [search, setSearch] = useState('');

  // What is RECORDED, so the editor can say how many rows differ from it.
  const stored = useMemo(() => {
    const next: Record<string, Effect> = {};
    for (const key of user.grantedPermissions) next[key] = 'grant';
    for (const key of user.revokedPermissions) next[key] = 'revoke';
    return next;
  }, [user.grantedPermissions, user.revokedPermissions]);

  // Reset whenever the user's stored overrides change, so the editor always
  // starts from what is actually recorded rather than from stale local state.
  useEffect(() => {
    setEffects(stored);
    setIsSaved(false);
  }, [stored]);

  const roleDefaults = useMemo(
    () => new Set(user.rolePermissions),
    [user.rolePermissions],
  );

  const allKeys = useMemo(
    () => (catalogue.data?.permissions ?? []).map((permission) => permission.key),
    [catalogue.data],
  );

  const save = useMutation({
    mutationFn: () =>
      usersApi.setPermissions(user.id, {
        grantedPermissions: Object.entries(effects)
          .filter(([, effect]) => effect === 'grant')
          .map(([key]) => key),
        revokedPermissions: Object.entries(effects)
          .filter(([, effect]) => effect === 'revoke')
          .map(([key]) => key),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: async () => {
      setIsSaved(true);
      setReason('');
      await onSaved();
    },
  });

  if (catalogue.isLoading) return <LoadingState label={t('common.loading')} />;
  if (catalogue.error) {
    return <ErrorState error={catalogue.error} onRetry={() => void catalogue.refetch()} />;
  }

  const effectOf = (key: string): Effect => effects[key] ?? 'default';
  const isChanged = (key: string) => effectOf(key) !== (stored[key] ?? 'default');

  const setEffect = (key: string, effect: Effect) => {
    setIsSaved(false);
    setEffects((current) => {
      const next = { ...current };
      if (effect === 'default') delete next[key];
      else next[key] = effect;
      return next;
    });
  };

  /** What the backend would resolve for this key, given the current choices. */
  const isEffective = (key: string): boolean => {
    const effect = effectOf(key);
    if (effect === 'grant') return true;
    if (effect === 'revoke') return false;
    return roleDefaults.has(key);
  };

  const needle = search.trim().toLowerCase();
  const visibleKeys = needle
    ? allKeys.filter(
        (key) =>
          key.toLowerCase().includes(needle) ||
          t(`permissionNames.${key}`, { defaultValue: key }).toLowerCase().includes(needle),
      )
    : allKeys;
  const groups = groupByModule(visibleKeys);
  const changedTotal = allKeys.filter(isChanged).length;
  const effectiveTotal = allKeys.filter(isEffective).length;

  const effectLabel: Record<Effect, string> = {
    default: t('permissions.default'),
    grant: t('permissions.grant'),
    revoke: t('permissions.revoke'),
  };

  return (
    <div className="flex min-w-0 flex-col gap-stack">
      <Alert tone="info" title={t('permissions.reviewApproveNotice')} />

      <div className="flex flex-wrap items-end justify-between gap-stack">
        <p className="text-body font-medium text-default-800">
          {t('permissions.summary', { count: effectiveTotal, total: allKeys.length })}
        </p>
        <SearchField
          value={search}
          onChange={setSearch}
          label={t('permissions.searchPlaceholder')}
        />
      </div>

      {groups.length === 0 ? (
        <p className="text-body text-default-600">{t('permissions.noMatch')}</p>
      ) : (
        <Accordion
          // Searching opens every module that has a match; otherwise the
          // person opens what they came for.
          key={needle ? 'searching' : 'browsing'}
          selectionMode="multiple"
          variant="splitted"
          {...(needle ? { defaultExpandedKeys: groups.map((group) => group.module) } : {})}
          itemClasses={{
            base: 'shadow-none border border-default-200',
            title: 'text-body font-semibold text-default-900',
            subtitle: 'text-caption text-default-600',
            trigger: 'py-3',
            content: 'pt-0 pb-2',
          }}
          className="px-0"
        >
          {groups.map((group) => {
            const allowed = group.keys.filter(isEffective).length;
            const changed = group.keys.filter(isChanged).length;
            return (
              <AccordionItem
                key={group.module}
                aria-label={t(`permissions.modules.${group.module}`)}
                title={t(`permissions.modules.${group.module}`)}
                subtitle={
                  <span className="flex flex-wrap items-center gap-inline">
                    <span>
                      {t('permissions.effectiveCount', {
                        count: allowed,
                        total: group.keys.length,
                      })}
                    </span>
                    {changed > 0 ? (
                      <StatusBadge
                        tone="warning"
                        label={t('permissions.changedCount', { count: changed })}
                      />
                    ) : null}
                  </span>
                }
              >
                <ul className="divide-y divide-default-100">
                  {group.keys.map((key) => {
                    const fromRole = roleDefaults.has(key);
                    const effective = isEffective(key);
                    const isSeparationKey =
                      key === PERMISSIONS.RESULTS_REVIEW ||
                      key === PERMISSIONS.RESULTS_APPROVE;
                    const name = t(`permissionNames.${key}`, { defaultValue: key });

                    return (
                      <li
                        key={key}
                        className={`grid min-w-0 gap-2 py-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center ${
                          isChanged(key) ? 'bg-warning-50/60' : ''
                        }`}
                      >
                        <div className="flex min-w-0 flex-col gap-1">
                          <PermissionName permissionKey={key} />
                          <div className="flex flex-wrap items-center gap-inline text-caption text-default-600">
                            <StatusBadge
                              tone={effective ? 'success' : 'neutral'}
                              label={effective ? t('permissions.allowed') : t('permissions.notAllowed')}
                            />
                            <span>{fromRole ? t('permissions.inRole') : t('permissions.notInRole')}</span>
                            {isSeparationKey ? (
                              // Review and approval are flagged where they are
                              // set, not only in the notice above: they are the
                              // two a manager must never treat as one switch.
                              <StatusBadge tone="warning" label={t('permissions.separateDuty')} />
                            ) : null}
                          </div>
                        </div>

                        {canEdit ? (
                          <SegmentedControl
                            label={t('permissions.choiceLabel', { name })}
                            value={effectOf(key)}
                            onChange={(next) => setEffect(key, next)}
                            options={[
                              { value: 'default', label: effectLabel.default },
                              { value: 'grant', label: effectLabel.grant, tone: 'success' },
                              { value: 'revoke', label: effectLabel.revoke, tone: 'danger' },
                            ]}
                          />
                        ) : (
                          <span className="text-caption text-default-700">
                            {effectOf(key) === 'grant'
                              ? t('permissions.granted')
                              : effectOf(key) === 'revoke'
                                ? t('permissions.revoked')
                                : t('permissions.default')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <p className="text-caption text-default-600">{t('permissions.backendNotice')}</p>

      {canEdit ? (
        <div className="flex flex-col gap-stack border-t border-default-200 pt-stack">
          <TextAreaField
            label={t('common.reasonOptional')}
            value={reason}
            onChange={setReason}
            rows={2}
          />

          {save.error ? <ErrorState error={save.error} /> : null}
          {changedTotal > 0 ? (
            <Alert
              tone="warning"
              title={t('permissions.unsaved')}
              description={t('permissions.changedCount', { count: changedTotal })}
            />
          ) : isSaved ? (
            <p role="status">
              <StatusBadge tone="success" label={t('common.saved')} />
            </p>
          ) : null}

          <ActionBar>
            <ActionButton
              priority="tertiary"
              isDisabled={changedTotal === 0 || save.isPending}
              onPress={() => setEffects(stored)}
            >
              {t('common.reset')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isLoading={save.isPending}
              isDisabled={changedTotal === 0}
              onPress={() => save.mutate()}
            >
              {t('permissions.save')}
            </ActionButton>
          </ActionBar>
        </div>
      ) : (
        <p className="text-caption text-default-600">{t('permissions.readOnlyNotice')}</p>
      )}
    </div>
  );
}
